import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/Drivers.css';

interface Driver {
  _id: string;
  firstName: string;
  lastName: string;
  phone: string;
  email?: string;
  licenseNumber: string;
  vehicleType: string;
  vehiclePlate: string;
  vehicleMake: string;
  vehicleModel: string;
  vehicleYear: number;
  location?: string;
  aadhaarNumber?: string;
  loyaltyPoints: number;
  rating: number;
  totalJobs: number;
  isOnline: boolean;
  isActive: boolean;
  onboardingCompleted?: boolean;
}

const Drivers: React.FC = () => {
  const [searchParams] = useSearchParams();
  const urlFilter = searchParams.get('filter') as 'all' | 'online' | 'offline' | null;

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'online' | 'offline'>(urlFilter || 'all');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [editingPoints, setEditingPoints] = useState<string | null>(null);
  const [pointsInput, setPointsInput] = useState('');

  const locations = ['Ernakulam', 'Kozhikode', 'Alappuzha', 'Trivandrum'];

  useEffect(() => {
    fetchDrivers();
  }, [filter, activeFilter, selectedLocation, page]);

  const fetchDrivers = async () => {
    setIsLoading(true);
    try {
      const params: any = { page, limit: 20 };

      if (filter === 'online') params.isOnline = true;
      if (filter === 'offline') params.isOnline = false;
      if (activeFilter === 'active') params.isActive = true;
      if (activeFilter === 'inactive') params.isActive = false;
      if (selectedLocation !== 'all') params.location = selectedLocation;

      const data = await api.getDrivers(params);
      setDrivers(data.drivers);
      setTotalPages(data.pagination.pages);
      setError('');
    } catch (err: any) {
      setError('Failed to load drivers');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (driverId: string, currentStatus: boolean) => {
    if (!window.confirm(`Are you sure you want to ${currentStatus ? 'deactivate' : 'activate'} this driver?`)) {
      return;
    }

    try {
      await api.toggleDriverStatus(driverId, !currentStatus);
      fetchDrivers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update driver status');
    }
  };

  const handleUpdatePoints = async (driverId: string) => {
    const points = parseInt(pointsInput);
    if (isNaN(points)) {
      alert('Please enter a valid number');
      return;
    }

    try {
      await api.updateDriverLoyalty(driverId, points);
      setEditingPoints(null);
      setPointsInput('');
      fetchDrivers();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update loyalty points');
    }
  };

  const startEditingPoints = (driverId: string) => {
    setEditingPoints(driverId);
    setPointsInput('0');
  };

  const cancelEditingPoints = () => {
    setEditingPoints(null);
    setPointsInput('');
  };

  if (isLoading && drivers.length === 0) {
    return (
      <div className="drivers-page">
        <div className="loading">Loading drivers...</div>
      </div>
    );
  }

  return (
    <div className="drivers-page">
      <div className="page-header">
        <div>
          <h1>Driver Management</h1>
          <p>Manage all registered drivers</p>
        </div>
        <button className="refresh-button" onClick={fetchDrivers}>
          🔄 Refresh
        </button>
      </div>

      {error && <div className="error-box">{error}</div>}

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <label>Online Status:</label>
          <select value={filter} onChange={(e) => { setFilter(e.target.value as any); setPage(1); }}>
            <option value="all">All Drivers</option>
            <option value="online">Online Only</option>
            <option value="offline">Offline Only</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Account Status:</label>
          <select value={activeFilter} onChange={(e) => { setActiveFilter(e.target.value as any); setPage(1); }}>
            <option value="all">All Drivers</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>
        </div>

        <div className="filter-group">
          <label>Location:</label>
          <select value={selectedLocation} onChange={(e) => { setSelectedLocation(e.target.value); setPage(1); }}>
            <option value="all">All Locations</option>
            {locations.map(loc => (
              <option key={loc} value={loc}>{loc}</option>
            ))}
          </select>
        </div>

        <div className="results-count">
          {drivers.length} driver{drivers.length !== 1 ? 's' : ''} found
        </div>
      </div>

      {/* Desktop Table */}
      <div className="desktop-table-container">
        <div className="table-container">
          <table className="drivers-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Location</th>
              <th>Vehicle</th>
              <th>Plate</th>
              <th>Rating</th>
              <th>Jobs</th>
              <th>Points</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {drivers.map((driver) => (
              <tr key={driver._id} className={!driver.isActive ? 'inactive-row' : ''}>
                <td>
                  <span className={`status-badge ${driver.isOnline ? 'online' : 'offline'}`}>
                    {driver.isOnline ? '🟢 Online' : '⚫ Offline'}
                  </span>
                  {!driver.isActive && (
                    <span className="status-badge inactive">Deactivated</span>
                  )}
                </td>
                <td>
                  <div className="driver-name">
                    <strong>{driver.firstName} {driver.lastName}</strong>
                    {driver.onboardingCompleted && <span className="badge-verified">✓</span>}
                  </div>
                </td>
                <td>{driver.phone}</td>
                <td>{driver.location || '-'}</td>
                <td>{driver.vehicleMake} {driver.vehicleModel} ({driver.vehicleYear})</td>
                <td><strong>{driver.vehiclePlate}</strong></td>
                <td>
                  <span className="rating">⭐ {driver.rating.toFixed(1)}</span>
                </td>
                <td>{driver.totalJobs}</td>
                <td>
                  {editingPoints === driver._id ? (
                    <div className="points-edit">
                      <input
                        type="number"
                        value={pointsInput}
                        onChange={(e) => setPointsInput(e.target.value)}
                        placeholder="+/- points"
                        autoFocus
                      />
                      <button onClick={() => handleUpdatePoints(driver._id)} className="btn-save">
                        ✓
                      </button>
                      <button onClick={cancelEditingPoints} className="btn-cancel">
                        ✗
                      </button>
                    </div>
                  ) : (
                    <div className="points-display">
                      <span className="points-value">{driver.loyaltyPoints}</span>
                      <button onClick={() => startEditingPoints(driver._id)} className="btn-edit">
                        ✏️
                      </button>
                    </div>
                  )}
                </td>
                <td>
                  <button
                    onClick={() => handleToggleStatus(driver._id, driver.isActive)}
                    className={driver.isActive ? 'btn-deactivate' : 'btn-activate'}
                  >
                    {driver.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

          {drivers.length === 0 && !isLoading && (
            <div className="empty-state">
              <p>No drivers found</p>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-card-list">
        {drivers.map((driver) => (
          <div key={driver._id} className="data-card">
            <div className="data-card-header">
              <div>
                <div className="data-card-title">
                  {driver.firstName} {driver.lastName}
                  {driver.onboardingCompleted && <span className="badge-verified">✓</span>}
                </div>
                <div className="data-card-subtitle">{driver.phone}</div>
              </div>
              <div>
                <span className={`status-badge ${driver.isOnline ? 'online' : 'offline'}`}>
                  {driver.isOnline ? '🟢' : '⚫'}
                </span>
                {!driver.isActive && (
                  <span className="status-badge inactive" style={{ display: 'block', marginTop: '4px' }}>Inactive</span>
                )}
              </div>
            </div>

            <div className="data-card-body">
              <div className="data-card-row">
                <span className="data-card-label">Location:</span>
                <span className="data-card-value">{driver.location || '-'}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Vehicle:</span>
                <span className="data-card-value">
                  {driver.vehicleMake} {driver.vehicleModel} ({driver.vehicleYear})
                </span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Plate:</span>
                <span className="data-card-value"><strong>{driver.vehiclePlate}</strong></span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Rating:</span>
                <span className="data-card-value">⭐ {driver.rating.toFixed(1)}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Jobs:</span>
                <span className="data-card-value">{driver.totalJobs}</span>
              </div>
              <div className="data-card-row">
                <span className="data-card-label">Points:</span>
                <span className="data-card-value">
                  {editingPoints === driver._id ? (
                    <div className="points-edit" style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                      <input
                        type="number"
                        value={pointsInput}
                        onChange={(e) => setPointsInput(e.target.value)}
                        placeholder="+/- points"
                        autoFocus
                        style={{ width: '80px' }}
                      />
                      <button onClick={() => handleUpdatePoints(driver._id)} className="btn-save">
                        ✓
                      </button>
                      <button onClick={cancelEditingPoints} className="btn-cancel">
                        ✗
                      </button>
                    </div>
                  ) : (
                    <div className="points-display" style={{ display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <span className="points-value">{driver.loyaltyPoints}</span>
                      <button onClick={() => startEditingPoints(driver._id)} className="btn-edit">
                        ✏️
                      </button>
                    </div>
                  )}
                </span>
              </div>
            </div>

            <div className="data-card-actions">
              <button
                onClick={() => handleToggleStatus(driver._id, driver.isActive)}
                className={driver.isActive ? 'btn-deactivate' : 'btn-activate'}
              >
                {driver.isActive ? 'Deactivate' : 'Activate'}
              </button>
            </div>
          </div>
        ))}

        {drivers.length === 0 && !isLoading && (
          <div className="empty-state">
            <p>No drivers found</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default Drivers;
